/**
 * Upgrade to Pro — Full page (refined design matching mockup).
 * Two-column layout: config + features (left), Price Breakdown Summary (right).
 * Billing cycle, Total Agent Seats, Voice Credits/mo pickers; Confirm → Pay Now flow.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useBillingAndInvoice } from '../../contexts/BillingAndInvoiceContext';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  alpha,
  Button,
  Container,
  IconButton,
  InputBase,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as CheckIcon,
  Security as SecurityIcon,
  ArrowForward as ArrowForwardIcon,
  Remove as MinusIcon,
  Add as PlusIcon,
  CalendarMonth as CalendarIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import RazorPayPaymentModal, { PAYMENT_STATUS } from '../../components/common/RazorPayPaymentModal';

// Fallback when API not yet loaded; real options come from units.available_billing_cycle
const BILLING_OPTIONS_FALLBACK = [
  { months: 3, label: '3 Months', discountLabel: 'STANDARD RATE', discount: 0, bestValue: false },
  { months: 6, label: '6 Months', discountLabel: 'SAVE 10%', discount: 10, bestValue: false },
  { months: 12, label: '1 Year', discountLabel: 'SAVE 15%', discount: 15, bestValue: true },
];

const DEFAULT_MIN_AGENTS = 1; // fallback when API does not return current_active_user
const MIN_ADDITIONAL_AGENTS = 0; // minimum additional users allowed (0 = no minimum)
const MAX_AGENTS = 99999; // safety cap, not shown in UI
const MIN_CREDITS = 1000;
const MIN_ADDITIONAL_CREDITS = 0; // 0 = none; when adding, minimum is MIN_ADDITIONAL_CREDITS_WHEN_NON_ZERO
const MIN_ADDITIONAL_CREDITS_WHEN_NON_ZERO = 500;
const MAX_CREDITS = 999999; // safety cap, not shown in UI
const CREDITS_STEP = 500;

// Match backend: customer_invoice.py BILLING_CYCLE_DISCOUNT_PCT
const BILLING_CYCLE_DISCOUNT_PCT = { 3: 0, 6: 10, 12: 15 };

const POLL_INTERVAL_MS = 2500;
const POLL_MAX_ATTEMPTS = 120;

export default function UpgradeToProPage({ isModal, onClose }) {
  const navigate = useNavigate();
  const { getProformaUnits, confirmBilling, getInvoicePaymentStatus } = useBillingAndInvoice();
  const [data, setData] = useState(null); // Set from proforma-units API (current_active_user, per_user_cost, available_billing_cycle, pro_features, etc.)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentUrl, setPaymentUrl] = useState(null);
  const [pendingInvoiceNumber, setPendingInvoiceNumber] = useState(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(PAYMENT_STATUS.IDLE);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [refreshStatusLoading, setRefreshStatusLoading] = useState(false);
  const [billingCycle, setBillingCycle] = useState(3);
  const [minAgents, setMinAgents] = useState(DEFAULT_MIN_AGENTS);
  const [additionalAgents, setAdditionalAgents] = useState(MIN_ADDITIONAL_AGENTS);
  const [additionalCredits, setAdditionalCredits] = useState(MIN_ADDITIONAL_CREDITS); // one-time extra credits (independent of seats)
  const [agentsEdit, setAgentsEdit] = useState(null);
  const [creditsEdit, setCreditsEdit] = useState(null);
  const hasSyncedFromUnits = useRef(false);
  const agentsInputRef = useRef(null);
  const creditsInputRef = useRef(null);

  const formatCurrency = (amount, currency = 'INR', decimals = 0) => {
    const symbol = currency === 'INR' ? '₹' : '$';
    const opts = decimals > 0 ? { minimumFractionDigits: decimals, maximumFractionDigits: decimals } : { minimumFractionDigits: 0, maximumFractionDigits: 0 };
    return `${symbol}${parseFloat(amount || 0).toLocaleString('en-IN', opts)}`;
  };

  // Initial load: fetch proforma-units only. All calculation done on FE from this data.
  const fetchProformaUnits = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getProformaUnits('upgrade');
      if (response.success) {
        setData(response.units || response.data);
      } else {
        setError(response.data.error || 'Failed to load upgrade options');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load upgrade options');
    } finally {
      setLoading(false);
    }
  }, [getProformaUnits]);

  useEffect(() => {
    fetchProformaUnits();
  }, [fetchProformaUnits]);

  // When payment completes (e.g. Razorpay redirect posts PAYMENT_SUCCESS), show success in modal.
  useEffect(() => {
    const handler = (event) => {
      if (event?.data?.type === 'PAYMENT_SUCCESS' && event?.origin === window.location.origin) {
        setPaymentStatus(PAYMENT_STATUS.SUCCESS);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  // Poll invoice status while payment modal is open; when paid, show success.
  useEffect(() => {
    if (!paymentModalOpen || !pendingInvoiceNumber || paymentStatus !== PAYMENT_STATUS.PENDING) return;
    let cancelled = false;
    let attempts = 0;
    const timerRef = { current: null };
    const poll = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > POLL_MAX_ATTEMPTS) return;
      const res = await getInvoicePaymentStatus(pendingInvoiceNumber);
      if (cancelled) return;
      if (res?.success && res?.paid) {
        setPaymentStatus(PAYMENT_STATUS.SUCCESS);
        return;
      }
      if (!cancelled) timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    };
    timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [paymentModalOpen, pendingInvoiceNumber, paymentStatus, getInvoicePaymentStatus]);

  // Sync minAgents from current_active_user; additional credits stay independent (only included recalculates from total_user)
  useEffect(() => {
    if (!data || hasSyncedFromUnits.current) return;
    const currentActiveUser = data.current_active_user != null ? Math.max(1, Number(data.current_active_user)) : DEFAULT_MIN_AGENTS;
    setMinAgents(currentActiveUser);
    setAdditionalAgents(MIN_ADDITIONAL_AGENTS);
    setAdditionalCredits(MIN_ADDITIONAL_CREDITS);
    setBillingCycle(3);
    hasSyncedFromUnits.current = true;
  }, [data?.current_active_user, data?.base_voice_credits_per_cycle]);

  // When user has a payment link but changes config (cycle, seats, credits), clear link so they must reconfirm.
  const prevConfigRef = useRef({ billingCycle, additionalAgents, additionalCredits });
  useEffect(() => {
    const prev = prevConfigRef.current;
    const changed = prev.billingCycle !== billingCycle || prev.additionalAgents !== additionalAgents || prev.additionalCredits !== additionalCredits;
    prevConfigRef.current = { billingCycle, additionalAgents, additionalCredits };
    if (paymentUrl && changed) setPaymentUrl(null);
  }, [billingCycle, additionalAgents, additionalCredits, paymentUrl]);

  // Billing cycle change: FE only, no API call
  const handleBillingCycleChange = (months) => {
    setBillingCycle(months);
  };

  const handleAgentsChange = (delta) => {
    const next = Math.min(MAX_AGENTS - minAgents, Math.max(MIN_ADDITIONAL_AGENTS, additionalAgents + delta));
    setAdditionalAgents(next);
  };

  const handleCreditsChange = (delta) => {
    let next = additionalCredits + delta * CREDITS_STEP;
    if (next <= 0) next = 0;
    else if (next < MIN_ADDITIONAL_CREDITS_WHEN_NON_ZERO) next = MIN_ADDITIONAL_CREDITS_WHEN_NON_ZERO;
    else next = Math.min(MAX_CREDITS, next);
    setAdditionalCredits(next);
  };

  const clampAdditionalAgents = (n) => Math.min(MAX_AGENTS - minAgents, Math.max(MIN_ADDITIONAL_AGENTS, Number(n) || MIN_ADDITIONAL_AGENTS));

  const handleAgentsFocus = () => {
    setAgentsEdit(String(additionalAgents));
    setTimeout(() => agentsInputRef.current?.select(), 0);
  };
  const handleAgentsChangeInput = (e) => setAgentsEdit(e.target.value);
  const handleAgentsBlur = () => {
    const n = parseInt(agentsEdit, 10);
    setAdditionalAgents(Number.isNaN(n) || agentsEdit === '' ? MIN_ADDITIONAL_AGENTS : clampAdditionalAgents(n));
    setAgentsEdit(null);
  };

  const handleCreditsFocus = () => {
    setCreditsEdit(String(additionalCredits));
    setTimeout(() => creditsInputRef.current?.select(), 0);
  };
  const handleCreditsChangeInput = (e) => setCreditsEdit(e.target.value);
  const handleCreditsBlur = () => {
    const n = parseInt(creditsEdit, 10);
    let next;
    if (creditsEdit === '' || Number.isNaN(n) || n < 0) next = 0;
    else if (n > 0 && n < MIN_ADDITIONAL_CREDITS_WHEN_NON_ZERO) next = MIN_ADDITIONAL_CREDITS_WHEN_NON_ZERO;
    else next = Math.min(MAX_CREDITS, n);
    setAdditionalCredits(next);
    setCreditsEdit(null);
  };

  // Included credits = f(total_user) only. Additional credits = independent state.
  const effectiveAdditionalAgents = agentsEdit !== null ? clampAdditionalAgents(parseInt(agentsEdit, 10) || MIN_ADDITIONAL_AGENTS) : additionalAgents;
  const effectiveAgents = minAgents + effectiveAdditionalAgents;
  const includedCreditsPerMonthFromUsers = effectiveAgents * (data?.base_voice_credits_per_cycle ?? 1000);
  const effectiveAdditionalCredits = creditsEdit !== null
    ? (() => {
        const n = parseInt(creditsEdit, 10);
        if (creditsEdit === '' || Number.isNaN(n) || n < 0) return 0;
        if (n > 0 && n < MIN_ADDITIONAL_CREDITS_WHEN_NON_ZERO) return MIN_ADDITIONAL_CREDITS_WHEN_NON_ZERO;
        return Math.min(MAX_CREDITS, n);
      })()
    : additionalCredits;
  const effectiveCredits = includedCreditsPerMonthFromUsers + effectiveAdditionalCredits;

  // Discount % from backend (billing_cycle_discount_pct) or fallback; keys may be number or string
  const getDiscountPct = useCallback((months) => {
    const pct = data?.billing_cycle_discount_pct;
    if (pct && typeof pct === 'object') {
      const v = pct[months] ?? pct[String(months)];
      if (v != null) return Number(v);
    }
    return BILLING_CYCLE_DISCOUNT_PCT[months] ?? 0;
  }, [data?.billing_cycle_discount_pct]);

  // Compute totals and line items on the UI (no backend call); uses effective values so price updates as user types.
  // When additional users or additional credits are added, show them as separate line items in the breakup.
  const computed = useMemo(() => {
    if (!data) return null;
    const months = billingCycle;
    const discountPctDecimal = getDiscountPct(months) / 100;
    const perUser = Number(data.per_user_cost);
    const perCredit = Number(data.per_credit_cost);
    const taxRate = Number(data.tax_rate);
    const baseCreditsPerUser = Number(data.base_voice_credits_per_cycle);

    // Discount is applied on total amount, not on per_user_cost — show full per_user_cost in line items.
    const cycleOpt = data?.billing_cycle?.find((o) => (o.months ?? 0) === months)
      || data?.available_billing_cycle?.find((o) => (o.months ?? 0) === months)
      || {};
    const discountedCreditCost = cycleOpt.discounted_credit_cost != null && Number(cycleOpt.discounted_credit_cost) >= 0
      ? Number(cycleOpt.discounted_credit_cost)
      : perCredit;

    const effectiveAdditionalAgents = Math.max(0, effectiveAgents - minAgents);
    const includedCreditsPerMonthFromUsers = effectiveAgents * baseCreditsPerUser;
    const additionalCreditsPerMonth = Math.max(0, effectiveCredits - includedCreditsPerMonthFromUsers);

    const baseTotalUser = perUser * effectiveAgents * months;
    const numCreditsTotal = effectiveCredits * months;
    const includedCreditsQty = includedCreditsPerMonthFromUsers * months;
    const additionalCreditsQty = additionalCreditsPerMonth;
    const totalCreditCost = Math.round(additionalCreditsQty * discountedCreditCost * 100) / 100;

    // Original amount = seats + additional credits (before discount). Discount applies to full basket.
    const originalAmount = baseTotalUser + totalCreditCost;
    const savingsAmount = Math.round(originalAmount * discountPctDecimal * 100) / 100;
    const afterDiscountUser = Math.round(baseTotalUser * (1 - discountPctDecimal) * 100) / 100;

    const subtotal = Math.round((originalAmount - savingsAmount) * 100) / 100;
    const taxAmount = Math.round(subtotal * taxRate * 100) / 100;
    const total = subtotal + taxAmount;

    const lineItems = [];

    // User Seats — full per_user_cost; quantity = seats, unit price = per seat for full cycle, amount = full total
    const perSeatPerCycle = perUser * months;
    lineItems.push({
      description: 'User Seats',
      quantity: effectiveAgents,
      unit_price: perSeatPerCycle,
      total_amount: Math.round(baseTotalUser * 100) / 100,
      tenure_months: months,
      per_user: perUser,
    });

    // Credits: included (free); additional = one-time prepaid (quantity as-is, no × cycle)
    if (additionalCreditsPerMonth > 0 && additionalCreditsQty > 0) {

      lineItems.push(
        { 
          description: 'Free Credits',
          quantity: includedCreditsQty,
          unit_price: 0,
          total_amount: 0,
        },
        { 
          description: 'Additional Credits', 
          quantity: additionalCreditsQty, 
          unit_price: Number(data?.per_credit_cost),
          total_amount: Math.round(additionalCreditsQty * Number(data?.per_credit_cost) * 100) / 100,
        },
      );
    } else {
      lineItems.push({ description: 'Free Credits', quantity: numCreditsTotal, unit_price: 0, total_amount: 0 });
    }

    const userTotalAmount = Math.round(afterDiscountUser * 100) / 100;
    const creditsTotalAmount = totalCreditCost;

    return {
      line_items: lineItems,
      user_total_amount: userTotalAmount,
      credits_total_amount: creditsTotalAmount,
      subtotal,
      tax_amount: taxAmount,
      total,
      savings_amount: savingsAmount,
      discount_pct: getDiscountPct(months),
      billing_cycle_months: months,
      actual_price: originalAmount,
      tax_rate: taxRate,
      currency: data.currency ?? 'INR',
      action_params: data.action_params ?? {},
    };
  }, [data, billingCycle, effectiveAgents, effectiveCredits, minAgents, getDiscountPct]);


  // 4) Confirm: POST /billing/confirm/ with flat body { action, plan_id, total_user, additional_credits, billing_cycle }
  const handleConfirm = async () => {
    if (!data?.plan_id) return;
    setConfirmLoading(true);
    setConfirmError(null);
    try {
      const additionalCreditsOneTime = Math.max(0, Math.round(effectiveCredits - includedCreditsPerMonthFromUsers));
      const payload = {
        action: 'upgrade',
        plan_id: Number(data.plan_id),
        total_user: Math.max(1, Math.floor(effectiveAgents)),
        additional_credits: additionalCreditsOneTime,
        billing_cycle: Number(billingCycle) || 3,
      };
      const res = await confirmBilling(payload);
      const paymentLink = res?.invoice?.payment_link ?? res?.payment_url;
      if (res?.success && paymentLink) {
        setPaymentUrl(paymentLink);
        setPendingInvoiceNumber(res?.invoice?.invoice_number ?? res?.invoice_number ?? null);
      } else {
        setConfirmError(res?.error || res?.detail || 'Failed to create payment link');
      }
    } catch (err) {
      setConfirmError(err?.response?.data?.error || err?.response?.data?.detail || err?.message || 'Failed to create payment link');
    } finally {
      setConfirmLoading(false);
    }
  };

  // 5) Pay Now: open RazorPay payment modal (iframe inside modal)
  const handlePayNow = () => {
    if (!paymentUrl || paymentModalOpen) return;
    setPaymentStatus(PAYMENT_STATUS.PENDING);
    setPaymentModalOpen(true);
  };

  const handlePaymentModalClose = () => {
    if (paymentStatus === PAYMENT_STATUS.PENDING) {
      setPaymentStatus(PAYMENT_STATUS.FAILED);
      return;
    }
    handleFullClose();
  };

  const handleFullClose = useCallback(() => {
    setPaymentModalOpen(false);
    setPaymentUrl(null);
    setPendingInvoiceNumber(null);
    setPaymentStatus(PAYMENT_STATUS.IDLE);
  }, []);

  const handleGoToSubscription = useCallback(() => {
    handleFullClose();
    if (isModal && onClose) onClose();
    navigate('/settings?tab=subscription', { replace: true });
  }, [handleFullClose, navigate, isModal, onClose]);

  const handleRetry = useCallback(() => {
    setPaymentStatus(PAYMENT_STATUS.PENDING);
  }, []);

  const handleRefreshStatus = useCallback(async () => {
    if (!pendingInvoiceNumber) return;
    setRefreshStatusLoading(true);
    try {
      const res = await getInvoicePaymentStatus(pendingInvoiceNumber);
      if (res?.success && res?.paid) setPaymentStatus(PAYMENT_STATUS.SUCCESS);
    } finally {
      setRefreshStatusLoading(false);
    }
  }, [pendingInvoiceNumber, getInvoicePaymentStatus]);

  const display = computed || data;
  const taxRate = (display?.tax_rate ?? 0) * 100;
  const totalDisplay = display?.total ?? 0;
  const subtotalDisplay = display?.subtotal ?? 0;
  const billingCycleMonths = display?.billing_cycle_months ?? billingCycle;
  const savingsAmount = display?.savings_amount ?? 0;
  const discountPct = display?.discount_pct ?? 0;
  const actualPriceDisplay = display?.actual_price ?? (discountPct > 0 ? (subtotalDisplay + (savingsAmount || 0)) : subtotalDisplay);
  const perUserCost = Number(data?.per_user_cost);
  const perCreditCost = Number(data?.per_credit_cost);
  const currency = display?.currency ?? data?.currency ?? 'INR';
  const taxAmountDisplay = display?.tax_amount ?? data?.tax_amount ?? 0;
  const includedCreditsPerMonth = includedCreditsPerMonthFromUsers;
  const additionalCreditsPerMonth = effectiveAdditionalCredits;

  // Design tokens
  const slate = { 50: '#f8fafc', 100: '#f1f5f9', 200: '#e2e8f0', 500: '#64748b', 600: '#475569', 700: '#334155', 800: '#1e293b', 900: '#0f172a', 950: '#020617' };
  const emerald = { 50: '#ecfdf5', 100: '#d1fae5', 200: '#a7f3d0', 300: '#6ee7b7', 600: '#059669', 700: '#047857', 800: '#065f46', 900: '#064e3b' };
  const sky200 = '#bae6fd';
  const softShadow = '0 1px 1px rgba(15,23,42,0.04), 0 10px 30px rgba(15,23,42,0.08)';
  const grainSvg = "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.6' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='120' height='120' filter='url(%23n)' opacity='.12' fill='%23d1d5db'/%3E%3C/svg%3E\")";

  return (
    <Box
      className="upgrade-pro-page"
      sx={{
        minHeight: '100vh',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          opacity: 0.08,
          mixBlendMode: 'multiply',
          backgroundImage: grainSvg,
          backgroundSize: '160px 160px',
        },
      }}
    >
      {/* Decorative blur circles + radial gradient (from HTML) */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', left: -160, top: -160, width: 520, height: 520, borderRadius: '50%', bgcolor: alpha(emerald[200], 0.35), filter: 'blur(64px)' }} />
        <Box sx={{ position: 'absolute', right: -128, top: 96, width: 560, height: 560, borderRadius: '50%', bgcolor: alpha(sky200, 0.35), filter: 'blur(64px)' }} />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 20% 0%, rgba(16,185,129,0.12), transparent 55%), radial-gradient(circle at 90% 20%, rgba(56,189,248,0.12), transparent 55%)',
          }}
        />
      </Box>

      <Container
        maxWidth="lg"
        sx={{
          position: 'relative',
          zIndex: 1,
          py: 2.5,
          px: { xs: 1.5, sm: 2 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        <Button
          component={isModal ? undefined : RouterLink}
          to={isModal ? undefined : '/settings?tab=subscription'}
          href={undefined}
          startIcon={<BackIcon />}
          onClick={isModal && onClose ? () => { onClose(); navigate('/settings?tab=subscription', { replace: true }); } : undefined}
          sx={{ mb: 1.5, textTransform: 'none', color: slate[800], fontSize: '0.9rem', alignSelf: 'flex-start' }}
        >
          Back to Subscription
        </Button>

        {loading && !data && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: emerald[600] }} />
            <Typography sx={{ ml: 1.5, fontSize: '0.9rem', color: slate[600] }}>Loading upgrade options...</Typography>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Initial load: show loader until proforma-units is loaded */}
        {loading && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              height: 3,
              zIndex: 1300,
              bgcolor: alpha(emerald[200], 0.3),
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: '40%',
                bgcolor: emerald[600],
                animation: 'shimmer 1.2s ease-in-out infinite',
                '@keyframes shimmer': {
                  '0%': { transform: 'translateX(-100%)' },
                  '100%': { transform: 'translateX(350%)' },
                },
              }}
            />
          </Box>
        )}

        {data && (
          <>
            {/* Header — badge + title + subtitle (HTML design) */}
            <Box sx={{ maxWidth: 672, mb: 0.5 }}>

              <Typography
                sx={{
                  fontFamily: 'Georgia, "Libre Baskerville", serif',
                  fontSize: { xs: '2.25rem', sm: '3rem' },
                  fontWeight: 700,
                  letterSpacing: '-0.025em',
                  color: slate[900],
                  lineHeight: 1.2,
                  mb: 0.75,
                }}
              >
                Scale Your <Box component="span" sx={{ color: emerald[700] }}>Sales Intelligence.</Box>
              </Typography>
              <Typography sx={{ color: slate[600], maxWidth: 576, fontSize: { xs: '0.875rem', sm: '1rem' }, lineHeight: 1.6 }}>
                Upgrade to Pro to unlock advanced AI-powered lead scoring, automated calling, and intelligent property matching workflows.
              </Typography>
            </Box>

            {/* Two columns: 1.2fr 0.9fr on lg — page scrolls; Investment Summary stays sticky */}
            <Box
              sx={{
                mt: 3,
                display: 'grid',
                gap: 2,
                gridTemplateColumns: { xs: '1fr', lg: '1.2fr 0.9fr' },
                alignItems: 'start',
              }}
            >
              {/* Left — configurator card (no inner scroll; page scrolls) */}
              <Box sx={{ minWidth: 0 }}>
                <Box
                  sx={{
                    borderRadius: '16px',
                    border: '1px solid',
                    borderColor: slate[200],
                    bgcolor: alpha('#fff', 0.6),
                    backdropFilter: 'blur(8px)',
                    p: { xs: 2, sm: 2.5 },
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0,
                  }}
                >
                  {/* Billing Cycle — green check icon, 3 cards */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                      <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: emerald[50], border: '1px solid', borderColor: emerald[100], display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckIcon sx={{ color: emerald[800], fontSize: '1rem' }} />
                      </Box>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: slate[800] }}>Billing Cycle</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
                      {(data?.billing_cycle ?? data?.available_billing_cycle ?? BILLING_OPTIONS_FALLBACK).map((opt, idx) => {
                        const months = opt.months ?? (opt.value === '6_month' || opt.value === 180 ? 6 : opt.value === '12_month' || opt.value === 365 ? 12 : 3);
                        const isSelected = billingCycleMonths === months;
                        const bestValue = opt.bestValue != null ? Boolean(opt.bestValue) : months === 12;
                        const hasDiscount = (opt.discount ?? 0) > 0;
                        const headerLabel = opt.discount_label ?? opt.discountLabel ?? (hasDiscount ? null : 'STANDARD RATE');
                        return (
                          <Box
                            key={opt.months ?? opt.value ?? idx}
                            component="button"
                            type="button"
                            onClick={() => handleBillingCycleChange(months)}
                            disabled={loading}
                            sx={{
                              position: 'relative',
                              width: '100%',
                              p: 1.5,
                              border: '1px solid',
                              borderColor: isSelected ? emerald[600] : slate[200],
                              boxShadow: isSelected ? '0 10px 30px rgba(16,185,129,0.14)' : 'none',
                              bgcolor: alpha('#fff', 0.7),
                              backdropFilter: 'blur(8px)',
                              borderRadius: '12px',
                              textAlign: 'left',
                              cursor: loading ? 'wait' : 'pointer',
                              transition: 'all 0.2s',
                              '&:hover': loading ? {} : { bgcolor: '#fff', borderColor: isSelected ? emerald[600] : slate[300] },
                              '&:active': { transform: 'scale(0.99)' },
                            }}
                          >
                            {bestValue && (
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  bgcolor: emerald[600],
                                  color: '#fff',
                                  px: 1.25,
                                  py: 0.25,
                                  borderRadius: '9999px',
                                  fontSize: '10px',
                                  fontWeight: 600,
                                  letterSpacing: 0.5,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                MOST POPULAR
                              </Box>
                            )}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
                              <CalendarIcon sx={{ fontSize: '1rem', color: isSelected ? emerald[600] : slate[400] }} />
                              {headerLabel && (
                                <Typography sx={{ fontSize: '11px', fontWeight: 600, color: slate[500], letterSpacing: 0.5 }}>{headerLabel}</Typography>
                              )}
                            </Box>
                            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: slate[900] }}>{opt.label}</Typography>
                            {hasDiscount && (
                              <Typography sx={{ fontSize: '0.7rem', color: emerald[600], mt: 0.5, fontWeight: 600 }}>
                                {opt.discount}% off total
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Total Agent Seats: left = title + cost info + ADDITIONAL stepper in grey box; right = summary + total cost */}
                  <Box sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: slate[200],
                        bgcolor: alpha('#fff', 0.7),
                        backdropFilter: 'blur(8px)',
                        p: 1.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: slate[900], mb: 0.5 }}>Total Agent Seats</Typography>
                      
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(160px, auto)' },
                          gap: { xs: 1.5, sm: 0 },
                          alignItems: 'start',
                        }}
                      >
                        {/* Column 1: config — cost info + ADDITIONAL stepper */}
                        <Box sx={{ minWidth: 0, pr: { sm: 1.5 } }}>
                          <Typography sx={{ fontSize: '0.75rem', color:"text.secondary", mt: 0.25, fontWeight: 600 }}>Per User Cost: <strong style={{ color: "primary.main" }}>{formatCurrency(perUserCost, currency)}</strong> / month</Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              flexWrap: 'nowrap',
                              gap: 1,
                              mt: 1.25,
                              minHeight: { xs: 52, sm: 56 },
                              height: { xs: 52, sm: 56 },
                              minWidth: { xs: '100%', sm: 320 },
                              width: { xs: '100%', sm: 320 },
                              flexShrink: 0,
                              borderRadius: '10px',
                              bgcolor: '#f9fcff',
                              border: '1px solid',
                              borderColor: slate[200],
                              boxShadow: '0 1px 4px #f9fcff',
                              overflow: 'hidden',
                              px: { xs: 1, sm: 1.25 },
                              py: { xs: 0.875, sm: 1 },
                            }}
                          >
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: slate[700], letterSpacing: 0.5, whiteSpace: 'nowrap', flexShrink: 0 }}>ADDITIONAL USER SEATS</Typography>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: { xs: 0.25, sm: 0.5 },
                                borderRadius: '10px',
                                bgcolor: '#ffffff',
                                border: '1px solid',
                                borderColor: slate[200],
                                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                overflow: 'hidden',
                                flexShrink: 0,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleAgentsChange(-1)}
                                disabled={effectiveAdditionalAgents <= MIN_ADDITIONAL_AGENTS || loading}
                                sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, borderRadius: 0, bgcolor: 'transparent', color: slate[700], '&:hover': { bgcolor: slate[50] } }}
                              >
                                <MinusIcon sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }} />
                              </IconButton>
                              <InputBase
                                inputRef={agentsInputRef}
                                type="number"
                                inputProps={{ min: MIN_ADDITIONAL_AGENTS, max: MAX_AGENTS - minAgents, 'aria-label': 'Additional users' }}
                                value={agentsEdit !== null ? agentsEdit : String(additionalAgents)}
                                onFocus={handleAgentsFocus}
                                onChange={handleAgentsChangeInput}
                                onBlur={handleAgentsBlur}
                                disabled={loading}
                                sx={{ width: { xs: 44, sm: 52 }, bgcolor: '#ffffff', '& input': { textAlign: 'center', fontSize: { xs: '0.8125rem', sm: '0.875rem' }, fontWeight: 600, py: 0.5, border: 'none', borderRadius: 0, bgcolor: '#ffffff', color: slate[900], '&:focus': { outline: 'none' }, MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleAgentsChange(1)}
                                disabled={effectiveAdditionalAgents >= MAX_AGENTS - minAgents || loading}
                                sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, borderRadius: 0, bgcolor: 'transparent', color: slate[700], '&:hover': { bgcolor: slate[50] } }}
                              >
                                <PlusIcon sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }} />
                              </IconButton>
                            </Box>
                          </Box>
                          {MIN_ADDITIONAL_AGENTS > 0 && (
                            <Typography sx={{ fontSize: '0.7rem', color: slate[400], mt: 0.5 }}>Min. {MIN_ADDITIONAL_AGENTS} additional users allowed</Typography>
                          )}
                        </Box>
                        {/* Column 2: summary — Included Seats, Additional Purchased, Total Seats, Per Seat, Total Seat Cost */}
                        <Box sx={{ minWidth: 0, pl: { xs: 0, sm: 1.5 } }}>
                          <Box sx={{ minWidth: { xs: 120, sm: 160 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography component="span" sx={{ fontSize: '0.75rem', color: slate[600], flexShrink: 0 }}>Existing Seats</Typography>
                              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: slate[800], whiteSpace: 'nowrap' }}>{minAgents}</Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography component="span" sx={{ fontSize: '0.75rem', color: slate[600], flexShrink: 0 }}>New Seats Added</Typography>
                              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: slate[800], whiteSpace: 'nowrap' }}>{effectiveAdditionalAgents}</Typography>
                            </Box>
                            <Box sx={{ borderTop: '1px solid', borderColor: slate[200], pt: 0.75, mb: 0.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: slate[800], flexShrink: 0 }}>Total Seats</Typography>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: slate[800], whiteSpace: 'nowrap' }}>{effectiveAgents}</Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography sx={{ fontSize: '0.75rem', color: slate[600], flexShrink: 0 }}>Per Seat ({billingCycleMonths} Months)</Typography>
                              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: slate[800], whiteSpace: 'nowrap' }}>
                                {formatCurrency(perUserCost * billingCycleMonths, currency).replace(/^(₹|\$)/, '$1 ')}
                              </Typography>
                            </Box>
                            <Box sx={{ borderTop: '1px solid', borderColor: slate[300], pt: 0.75, mt: 0.25 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: slate[900], flexShrink: 0 }}>Total Seat Cost</Typography>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: slate[900], whiteSpace: 'nowrap' }}>
                                  {formatCurrency(effectiveAgents * perUserCost * billingCycleMonths, currency).replace(/^(₹|\$)/, '$1 ')}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          mt: 1,
                          px: 1.5,
                          py: 0.75,
                          borderRadius: '9999px',
                          bgcolor: 'rgba(147, 51, 234, 0.1)',
                          border: '1px solid rgba(147, 51, 234, 0.2)',
                        }}
                      >
                        <InfoIcon sx={{ fontSize: '1rem', color: 'rgb(126, 34, 206)' }} />
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'rgb(126, 34, 206)' }}>
                          <strong style={{ color: "primary.main" }}>{(data?.base_voice_credits_per_cycle).toLocaleString()} credits</strong> per seat/month.
                          Granted upfront for the selected term.
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                  {/* Voice Credits: left = title + cost info + ADDITIONAL CREDITS stepper in grey box; right = summary + total cost + duration */}
                  <Box sx={{ mb: 1.5 }}>
                    <Box
                      sx={{
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: slate[200],
                        bgcolor: alpha('#fff', 0.7),
                        backdropFilter: 'blur(8px)',
                        p: 1.5,
                      }}
                    >
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: slate[900], mb: 1 }}>Voice Credits</Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(160px, auto)' },
                          gap: { xs: 1.5, sm: 0 },
                          alignItems: 'start',
                        }}
                      >
                        {/* Column 1: config — cost info + ADDITIONAL CREDITS stepper */}
                        <Box sx={{ minWidth: 0, pr: { sm: 1.5 } }}>
                          <Typography sx={{ fontSize: '0.75rem', color: "green", fontWeight: 600 }}>Per Credit Cost: <strong>{formatCurrency(perCreditCost, currency, 2)}</strong></Typography>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              flexWrap: 'nowrap',
                              gap: 1,
                              mt: 1.25,
                              minHeight: { xs: 52, sm: 56 },
                              height: { xs: 52, sm: 56 },
                              minWidth: { xs: '100%', sm: 320 },
                              width: { xs: '100%', sm: 320 },
                              flexShrink: 0,
                              borderRadius: '10px',
                              bgcolor: '#f9fcff',
                              border: '1px solid',
                              borderColor: slate[200],
                              boxShadow: '0 1px 4px #f9fcff',
                              overflow: 'hidden',
                              px: { xs: 1, sm: 1.25 },
                              py: { xs: 0.875, sm: 1 },
                            }}
                          >
                            <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: slate[700], letterSpacing: 0.5, whiteSpace: 'nowrap', flexShrink: 0 }}>ADDITIONAL CREDITS</Typography>
                            <Box
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: { xs: 0.25, sm: 0.5 },
                                borderRadius: '10px',
                                bgcolor: '#ffffff',
                                border: '1px solid',
                                borderColor: slate[200],
                                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                                overflow: 'hidden',
                                flexShrink: 0,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleCreditsChange(-1)}
                                disabled={additionalCreditsPerMonth <= 0 || loading}
                                sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, borderRadius: 0, bgcolor: 'transparent', color: slate[700], '&:hover': { bgcolor: slate[50] } }}
                              >
                                <MinusIcon sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }} />
                              </IconButton>
                              <InputBase
                                inputRef={creditsInputRef}
                                type="number"
                                inputProps={{ min: 0, max: MAX_CREDITS, 'aria-label': 'Additional credits' }}
                                value={creditsEdit !== null ? creditsEdit : String(additionalCredits)}
                                onFocus={handleCreditsFocus}
                                onChange={handleCreditsChangeInput}
                                onBlur={handleCreditsBlur}
                                disabled={loading}
                                sx={{ width: { xs: 44, sm: 52 }, bgcolor: '#ffffff', '& input': { textAlign: 'center', fontSize: { xs: '0.8125rem', sm: '0.875rem' }, fontWeight: 600, py: 0.5, border: 'none', borderRadius: 0, bgcolor: '#ffffff', color: slate[900], '&:focus': { outline: 'none' }, MozAppearance: 'textfield' }, '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': { WebkitAppearance: 'none', margin: 0 } }}
                              />
                              <IconButton
                                size="small"
                                onClick={() => handleCreditsChange(1)}
                                disabled={effectiveCredits >= MAX_CREDITS || loading}
                                sx={{ width: { xs: 32, sm: 36 }, height: { xs: 32, sm: 36 }, borderRadius: 0, bgcolor: 'transparent', color: slate[700], '&:hover': { bgcolor: slate[50] } }}
                              >
                                <PlusIcon sx={{ fontSize: { xs: '0.8125rem', sm: '0.875rem' } }} />
                              </IconButton>
                            </Box>
                          </Box>
                        </Box>
                        {/* Column 2: summary — Included Credits, Additional Purchased, Total Available, AI Coverage, Cost */}
                        <Box sx={{ minWidth: 0, pl: { xs: 0, sm: 1.5 } }}>
                          <Box sx={{ minWidth: { xs: 120, sm: 160 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography component="span" sx={{ fontSize: '0.75rem', color: slate[600] }}>Included Free Credits</Typography>
                              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: slate[800] }}>
                                {(includedCreditsPerMonth * billingCycleMonths).toLocaleString()}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography component="span" sx={{ fontSize: '0.75rem', color: slate[600] }}>Additional Credits Purchased</Typography>
                              <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 600, color: slate[800] }}>
                                {additionalCreditsPerMonth.toLocaleString()}
                              </Typography>
                            </Box>
                            <Box sx={{ borderTop: '1px solid', borderColor: slate[200], pt: 0.75, mb: 0.5 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: slate[800] }}>Total Credits Available</Typography>
                                <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: slate[800] }}>
                                  {(includedCreditsPerMonth * billingCycleMonths + additionalCreditsPerMonth).toLocaleString()}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Typography sx={{ fontSize: '0.75rem', color: slate[500], whiteSpace: 'nowrap' }}>AI Analysis Coverage</Typography>
                              <Typography sx={{ fontSize: '0.75rem', color: slate[500], whiteSpace: 'nowrap' }}>
                                ≈ {(includedCreditsPerMonth * billingCycleMonths + additionalCreditsPerMonth) > 60
                                  ? `${((includedCreditsPerMonth * billingCycleMonths + additionalCreditsPerMonth) / 60).toLocaleString('en-IN', { maximumFractionDigits: 1 })} hrs`
                                  : `${(includedCreditsPerMonth * billingCycleMonths + additionalCreditsPerMonth)} min`}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, pt: 0.75, mt: 0.25, borderTop: '1px solid', borderColor: slate[300] }}>
                              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: slate[900], flexShrink: 0 }}>Cost for Additional Credits</Typography>
                              <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: slate[900], whiteSpace: 'nowrap' }}>
                                {formatCurrency(computed?.credits_total_amount ?? (additionalCreditsPerMonth * perCreditCost), currency).replace(/^(₹|\$)/, '$1 ')}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>

                      </Box>
                    </Box>
                  </Box>

                  {/* Included with Pro — from API pro_features (title, subtitle, items) */}
                  {data?.pro_features && (
                    <Box sx={{ mt: 1.5, borderRadius: '16px', border: '1px solid', borderColor: slate[200], bgcolor: alpha('#fff', 0.7), backdropFilter: 'blur(8px)', p: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 1 }}>
                        <Box>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: slate[900] }}>{data.pro_features.title}</Typography>
                          <Typography sx={{ fontSize: '0.75rem', color: slate[500], mt: 0.25 }}>{data.pro_features.subtitle}</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25 }}>
                        {([...(data.pro_features.items || [])]
                          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                          .map((item, i) => (
                            <Box
                              key={item.order ?? i}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.25,
                                borderRadius: '8px',
                                bgcolor: alpha(slate[50], 0.5),
                                border: '1px solid',
                                borderColor: slate[100],
                                p: 1.25,
                              }}
                            >
                              <Box sx={{ color: emerald[600], flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                                {item.icon === '✓' ? <CheckIcon sx={{ fontSize: '0.875rem' }} /> : <Typography component="span" sx={{ fontSize: '0.875rem', fontWeight: 600 }}>{item.icon}</Typography>}
                              </Box>
                              <Typography sx={{ fontSize: '12px', fontWeight: 500, color: slate[700] }}>{item.text}</Typography>
                            </Box>
                          )))}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Right — Price Breakup Summary (sticky; matches reference card) */}
              <Box sx={{ position: { lg: 'sticky' }, top: { lg: 24 }, alignSelf: { lg: 'flex-start' } }}>
                <Box
                  sx={{
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '1px solid',
                    borderColor: slate[200],
                    boxShadow: softShadow,
                    bgcolor: alpha('#fff', 0.7),
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  {/* Dark header — gradient, title, shield, pricing breakdown */}
                  <Box
                    sx={{
                      background: `linear-gradient(to bottom, ${slate[950]}, ${slate[900]})`,
                      color: '#fff',
                      px: 2,
                      py: 2.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, mb: 2 }}>
                      <Box>
                        <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>PRICING BREAKUP</Typography>
                        <Box
                          sx={{
                            mt: 0.75,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '8px',
                            bgcolor: 'rgba(255,255,255,0.12)',
                            border: '1px solid rgba(255,255,255,0.25)',
                          }}
                        >
                          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: 'rgba(255,255,255,0.9)', letterSpacing: 0.3 }}>
                            Billing Cycle:
                          </Typography>
                          <Typography sx={{ fontSize: '0.8rem', fontWeight: 800, color: emerald[200], letterSpacing: 0.3 }}>
                            {billingCycleMonths === 12 ? '1 Year' : `${billingCycleMonths} Months`}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <SecurityIcon sx={{ color: '#fff', fontSize: '1.25rem' }} />
                      </Box>
                    </Box>
                    <Box sx={{ pt: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                        <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.75)' }}>PRO</Typography>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 700 }}>{formatCurrency(actualPriceDisplay, currency)}</Typography>
                      </Box>
                      {discountPct > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                          <Typography sx={{ fontSize: '0.75rem', color: emerald[200] }}>APPLIED DISCOUNT ({discountPct}%)</Typography>
                          <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: emerald[200] }}>-{formatCurrency(savingsAmount, currency)}</Typography>
                        </Box>
                      )}
                      <Box sx={{ mt: 1.25, pt: 1.25, borderTop: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, letterSpacing: 0.5 }}>EFFECTIVE TOTAL</Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: emerald[300] }}>{formatCurrency(subtotalDisplay, currency)}</Typography>
                      </Box>
                    </Box>
                  </Box>
                  {/* Light body — line items, subtotal, tax, callout, total due, CTA, compliance, footer */}
                  <Box sx={{ px: 2, py: 2, bgcolor: '#fff' }}>
                    {(display?.line_items ?? []).length > 0 && (
                      <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: '1px solid', borderColor: slate[200] }}>
                        {/* Header */}
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 72px 120px 100px',
                            gap: 0.75,
                            alignItems: 'center',
                            mb: 0.75,
                            pb: 0.5,
                            borderBottom: '1px solid',
                            borderColor: slate[200],
                          }}
                        >
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, color: slate[500], textTransform: 'uppercase' }}>
                            Item
                          </Typography>
                          <Typography component="span" sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, color: slate[500], textTransform: 'uppercase', textAlign: 'right', display: 'block' }}>
                            Unit Price
                            <Box component="span" sx={{ display: 'block', fontSize: '0.6rem', fontWeight: 500, textTransform: 'none', letterSpacing: 0, mt: 0.25, whiteSpace: 'nowrap' }}>(Months × Unit Price)</Box>
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, color: slate[500], textTransform: 'uppercase', textAlign: 'right' }}>
                            Qty
                          </Typography>
                          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: 0.5, color: slate[500], textTransform: 'uppercase', textAlign: 'right' }}>
                            Amount
                          </Typography>
                        </Box>

                        {/* Items */}
                        {(display.line_items).map((item, idx) => (
                          <Box
                            key={idx}
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '1fr 72px 120px 100px',
                              gap: 0.75,
                              alignItems: 'center',
                              mb: idx < display.line_items.length - 1 ? 0.5 : 0,
                            }}
                          >
                            <Typography sx={{ fontSize: '0.8rem', color: slate[700] }}>{item.description}</Typography>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: slate[900], textAlign: 'right' }}>
                              {item.tenure_months != null && item.per_user != null
                                ? `${item.tenure_months} × ${formatCurrency(item.per_user, display?.currency ?? data?.currency)}`
                                : formatCurrency(item.unit_price ?? 0, display?.currency ?? data?.currency, 2)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: slate[700], textAlign: 'right' }}>
                              {item.quantity != null ? Number(item.quantity).toLocaleString() : '—'}
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: (item.total_amount ?? 0) < 0 ? emerald[700] : slate[900], textAlign: 'right' }}>
                              {(item.total_amount ?? 0) < 0 ? `-${formatCurrency(Math.abs(item.total_amount ?? 0), display?.currency ?? data?.currency)}` : formatCurrency(item.total_amount ?? 0, display?.currency ?? data?.currency)}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {/* Price summary: Subtotal (with optional discount line), Tax, Total */}
                    <Box
                      sx={{
                        borderRadius: '12px',
                        border: '1px solid',
                        borderColor: slate[200],
                        bgcolor: slate[50],
                        px: 1.5,
                        py: 1.5,
                        mb: 1.5,
                      }}
                    >
                      {savingsAmount > 0 && (
                        <>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                            <Typography sx={{ fontSize: '0.8125rem', color: slate[600] }}>Original Amount                            </Typography>
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: slate[800] }}>{formatCurrency(actualPriceDisplay, currency)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                            <Typography sx={{ fontSize: '0.8125rem', color: emerald[700], fontWeight: 600 }}>Billing Cycle Discount ({discountPct}%)</Typography>
                            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: emerald[700] }}>-{formatCurrency(savingsAmount, currency)}</Typography>
                          </Box>
                        </>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: slate[600], fontWeight: 600 }}>Subtotal</Typography>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: slate[900] }}>{formatCurrency(subtotalDisplay, currency)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                        <Typography sx={{ fontSize: '0.8125rem', color: slate[600] }}>Tax ({taxRate.toFixed(0)}%)</Typography>
                        <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: slate[900] }}>{formatCurrency(taxAmountDisplay, currency)}</Typography>
                      </Box>
                      <Box sx={{ borderTop: '1px solid', borderColor: slate[200], pt: 1.25, mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, letterSpacing: 0.5, color: emerald[700], textTransform: 'uppercase' }}>Total Payable</Typography>
                        <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, letterSpacing: '-0.025em', color: emerald[700] }}>{formatCurrency(totalDisplay, currency)}</Typography>
                      </Box>
                    </Box>
                    {savingsAmount > 0 && (
                      <Box
                        sx={{
                          mb: 1.5,
                          py: 0.75,
                          px: 1.25,
                          borderRadius: '10px',
                          border: '1px solid',
                          borderColor: emerald[100],
                          bgcolor: alpha(emerald[50], 0.6),
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckIcon sx={{ color: emerald[700], fontSize: '1.125rem' }} />
                          <Typography sx={{ fontSize: '0.75rem', color: emerald[800], fontWeight: 600 }}>You're saving {formatCurrency(savingsAmount, currency)} on this {billingCycleMonths === 12 ? 'annual' : ''} plan.</Typography>
                        </Box>
                      </Box>
                    )}
                    {confirmError && <Alert severity="error" sx={{ mb: 1.5 }}>{confirmError}</Alert>}
                    {!paymentUrl ? (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleConfirm}
                        disabled={confirmLoading}
                        endIcon={confirmLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <ArrowForwardIcon />}
                        sx={{
                          height: 48,
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '12px',
                          bgcolor: emerald[700],
                          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                          '&:hover': { bgcolor: emerald[800], boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
                        }}
                      >
                        {confirmLoading ? 'Creating...' : 'Confirm'}
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handlePayNow}
                        disabled={paymentModalOpen}
                        endIcon={<ArrowForwardIcon />}
                        sx={{
                          height: 48,
                          fontWeight: 600,
                          textTransform: 'none',
                          borderRadius: '12px',
                          bgcolor: emerald[700],
                          boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                          '&:hover': { bgcolor: emerald[800], boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
                          '&.Mui-disabled': { bgcolor: emerald[700], color: '#fff', opacity: 0.8 },
                        }}
                      >
                        Pay Now
                      </Button>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 1.25 }}>
                      <SecurityIcon sx={{ fontSize: '0.875rem', color: slate[400] }} />
                      <Typography sx={{ fontSize: '0.7rem', color: slate[500] }}>Secure 256-bit SSL Encrypted Payment</Typography>
                    </Box>
                  </Box>
                </Box>
                <Typography sx={{ display: 'block', mt: 1.5, fontSize: '0.7rem', lineHeight: 1.6, color: slate[500], textAlign: 'center', px: 0.5 }}>
                  Have a query? Please contact us at{' '}
                  <Box component="a" href="#" sx={{ color: emerald[700], fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                    hello@salesgro.ai
                  </Box>
                </Typography>
              </Box>
            </Box>

            <RazorPayPaymentModal
              open={paymentModalOpen && !!paymentUrl}
              onClose={handlePaymentModalClose}
              paymentUrl={paymentUrl}
              title="Complete payment — Upgrade to Pro"
              paymentStatus={paymentStatus}
              totalPayable={totalDisplay != null ? formatCurrency(totalDisplay, currency) : undefined}
              onRetry={handleRetry}
              onRefreshStatus={handleRefreshStatus}
              onGoToSubscription={handleGoToSubscription}
              onChangeMethod={handleFullClose}
              isRefreshingStatus={refreshStatusLoading}
            />
          </>
        )}
      </Container>
    </Box>
  );
}
